'use client'

import { useEffect, useRef } from 'react'
import { getPostHog, onPostHogReady } from '../../../instrumentation-client'
import { useStore } from '@/domain/state'
import { ConsentStatus } from '@/domain/state/analyticsConsent'

type PostHogInstance = NonNullable<ReturnType<typeof getPostHog>>
const PENDING_PERSISTENCE = 'memory'
const ACCEPTED_PERSISTENCE = 'localStorage+cookie'

function applyConsentToPostHog(posthog: PostHogInstance, status: ConsentStatus) {
  if (status === 'accepted') {
    posthog.set_config({ persistence: ACCEPTED_PERSISTENCE })
    // Full tracking with cookies and session recordings
    posthog.opt_in_capturing({ captureEventName: false })
    posthog.startSessionRecording()
  } else if (status === 'declined') {
    posthog.set_config({ persistence: PENDING_PERSISTENCE })
    // User explicitly declined — stop capturing entirely
    posthog.opt_out_capturing()
    posthog.stopSessionRecording()
  } else {
    posthog.set_config({ persistence: PENDING_PERSISTENCE })
    // 'pending': events are captured in-memory only until explicit consent.
    // Session recordings are disabled until explicit consent.
    posthog.stopSessionRecording()
  }
}

export function useAnalyticsConsent() {
  const status = useStore((state) => state.analyticsConsent.status)
  const statusRef = useRef(status)
  statusRef.current = status

  useEffect(() => {
    // Register callback to apply consent when PostHog loads
    // Uses ref to always get the latest status value
    const unsubscribe = onPostHogReady((posthog) => {
      applyConsentToPostHog(posthog, statusRef.current)
    })
    return unsubscribe
  }, [])

  useEffect(() => {
    // Apply consent immediately if PostHog is already loaded
    // Uses getPostHog() directly to avoid queuing stale callbacks
    const posthog = getPostHog()
    if (posthog) {
      applyConsentToPostHog(posthog, status)
    }
  }, [status])

  return { status }
}
